import React, { useEffect, useState } from "react"
import { View } from "react-native"
import LoanProductCard from "@/screens/loans/components/LoanProductCard"
import { type LoanApplicationType, type ProductType } from "@/generated/graphql"
import { capitalProductsListScrolled } from "@/analytics/events/loans"
import BFText, { Body } from "@/components/standard/BFText"
import { loanUpdatingState } from "@/stores/updateLoan"
import { useStore } from "@nanostores/react"
import { LoanProductButton } from "@/screens/loans/components/LoanProductButton"
import {
  type CombinedProductType,
  mergeLoanProducts,
} from "@/screens/loans/utils/LoanCapitalProducts"
import LoanProductCardCombined from "@/screens/loans/components/LoanProductCardCombined"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useUser } from "@/queries/user"

const LoanProductScroller = () => {
  const {
    loanId,
    loanApplication,
    capitalProducts,
    isLoading,
    isError,
    userHasRewards,
  } = useLoanContext()
  const { data: userData } = useUser()
  const [mergedLoanProducts, setMergedLoanProducts] = useState<
    ProductType[] | null
  >(null)
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)

  const loanUpdating: boolean = useStore(loanUpdatingState)

  useEffect(() => {
    if (!capitalProducts) {
      return
    }

    // Determine which cards to use based on user rewards
    const productsToUse = mergeLoanProducts(capitalProducts as ProductType[])
    setMergedLoanProducts(productsToUse)

    // Synchronize the currentIndex based on mergedLoanProducts
    if (productsToUse.length > 0 && loanApplication?.loanProduct) {
      const selectedIndex = productsToUse.findIndex(
        (product) => product?.product === loanApplication?.loanProduct
      )
      setCurrentIndex(selectedIndex >= 0 ? selectedIndex : 0)
    }
  }, [capitalProducts, loanApplication, userHasRewards])
  if (
    isLoading ||
    !capitalProducts ||
    !mergedLoanProducts ||
    !userData ||
    currentIndex === null
  ) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Body className="text-midnight-blue mt-2">Loading...</Body>
      </View>
    )
  }

  if (isError) {
    return <BFText>Error loading loan products</BFText>
  }

  const determineIfButtonSelected = (product?: string | null): boolean => {
    if (
      !product ||
      loanUpdating ||
      (loanApplication?.leverageDiscount === null &&
        product === "standard_combined")
    ) {
      return false
    }
    if (
      product === "standard_combined" &&
      loanApplication?.loanProduct === "standard"
    ) {
      return true
    }
    return product === loanApplication?.loanProduct
  }

  return (
    <View>
      {mergedLoanProducts?.length > 1 && (
        <View className={"flex-row gap-3 px-4 pb-4"}>
          {mergedLoanProducts?.map((lp, index) => (
            <LoanProductButton
              key={index}
              cardData={lp}
              isSelected={determineIfButtonSelected(lp.product)}
              isShown={index === currentIndex}
              onPress={() => {
                capitalProductsListScrolled({
                  scrolledTo: index + 1,
                  type: "click",
                })
                setCurrentIndex(index)
              }}
            />
          ))}
        </View>
      )}

      <View className={"px-4 w-full"}>
        {/* If the product is 'standard_combined', render the combined card */}
        {mergedLoanProducts[currentIndex]?.product === "standard_combined" ? (
          <LoanProductCardCombined
            key={currentIndex}
            loanId={loanId}
            cardData={mergedLoanProducts[currentIndex] as CombinedProductType}
            loanData={loanApplication as LoanApplicationType}
          />
        ) : (
          <LoanProductCard
            key={currentIndex}
            cardIndex={currentIndex}
            loanId={loanId}
            cardData={mergedLoanProducts[currentIndex]}
            loanData={loanApplication}
            isSelected={
              mergedLoanProducts[currentIndex]?.product ===
              loanApplication?.loanProduct
            }
          />
        )}
      </View>
    </View>
  )
}

export default LoanProductScroller
